import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Eye, EyeOff, ArrowLeft, CheckCircle } from 'lucide-react'
import { designTokens } from '@/utils/design-tokens'
import Input from '@/components/ui/Input'
import Button from '@/components/ui/Button'
import { AuthenticationService } from '@/api/generated/services/AuthenticationService'
import type { PasswordResetRequest } from '@/api/generated/models/PasswordResetRequest'
import type { PasswordResetConfirmRequest } from '@/api/generated/models/PasswordResetConfirmRequest'
import { useAlert } from '@/components/ui/Alert/exports'
import { handleApiError } from '@/utils/api-error-handler'

// Two-step flow states
type ResetStep = 'email' | 'verify'

const ResetPasswordPage: React.FC = () => {
  const navigate = useNavigate()
  const { showError, showSuccess } = useAlert()

  // Current step state
  const [currentStep, setCurrentStep] = useState<ResetStep>('email')

  // Step 1: Email form state
  const [email, setEmail] = useState('')
  const [emailError, setEmailError] = useState('')
  const [isRequestLoading, setIsRequestLoading] = useState(false)

  // Step 2: Verification form state
  const [token, setToken] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [tokenError, setTokenError] = useState('')
  const [newPasswordError, setNewPasswordError] = useState('')
  const [confirmPasswordError, setConfirmPasswordError] = useState('')
  const [isConfirmLoading, setIsConfirmLoading] = useState(false)

  // Password visibility states
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  // Note: localStorage clearing and logout is now handled by App.tsx for /reset-password route
  // This ensures proper route rendering by updating isAuthenticated state

  // Clear errors when user types
  useEffect(() => {
    setEmailError('')
  }, [email])

  useEffect(() => {
    setTokenError('')
  }, [token])

  useEffect(() => {
    setNewPasswordError('')
  }, [newPassword])

  useEffect(() => {
    setConfirmPasswordError('')
  }, [confirmPassword])

  // Step 1: Email validation
  const validateEmailForm = () => {
    let isValid = true

    setEmailError('')

    if (!email.trim()) {
      setEmailError('이메일을 입력해주세요.')
      isValid = false
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      setEmailError('올바른 이메일 형식을 입력해주세요.')
      isValid = false
    }

    return isValid
  }

  // Step 2: Verification form validation
  const validateVerificationForm = () => {
    let isValid = true

    setTokenError('')
    setNewPasswordError('')
    setConfirmPasswordError('')

    // Token validation
    if (!token.trim()) {
      setTokenError('인증번호를 입력해주세요.')
      isValid = false
    }

    // New password validation
    if (!newPassword.trim()) {
      setNewPasswordError('새 비밀번호를 입력해주세요.')
      isValid = false
    } else if (newPassword.length < 8) {
      setNewPasswordError('새 비밀번호는 최소 8자 이상이어야 합니다.')
      isValid = false
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(newPassword)) {
      setNewPasswordError('새 비밀번호는 대문자, 소문자, 숫자를 포함해야 합니다.')
      isValid = false
    }

    // Confirm password validation
    if (!confirmPassword.trim()) {
      setConfirmPasswordError('새 비밀번호 확인을 입력해주세요.')
      isValid = false
    } else if (newPassword !== confirmPassword) {
      setConfirmPasswordError('새 비밀번호와 확인 비밀번호가 일치하지 않습니다.')
      isValid = false
    }

    return isValid
  }

  // Step 1: Send reset email
  const handleSendResetEmail = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateEmailForm()) {
      return
    }

    setIsRequestLoading(true)

    try {
      const resetRequest: PasswordResetRequest = {
        email: email.trim()
      }

      await AuthenticationService.appsAuthenticationViewsPasswordResetRequest({
        requestBody: resetRequest
      })

      showSuccess('인증번호가 이메일로 전송되었습니다.')
      setCurrentStep('verify')
    } catch (error: any) {
      console.error('Failed to send reset email:', error)
      handleApiError(error, showError, '인증번호 전송에 실패했습니다')
    } finally {
      setIsRequestLoading(false)
    }
  }

  // Step 2: Confirm password reset
  const handleConfirmReset = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateVerificationForm()) {
      return
    }

    setIsConfirmLoading(true)

    try {
      const confirmRequest: PasswordResetConfirmRequest = {
        token: token.trim(),
        new_password: newPassword.trim(),
        confirm_password: confirmPassword.trim()
      }

      await AuthenticationService.appsAuthenticationViewsPasswordResetConfirm({
        requestBody: confirmRequest
      })

      showSuccess('비밀번호가 성공적으로 재설정되었습니다. 새 비밀번호로 로그인해주세요.')

      // Redirect to login page after successful reset
      setTimeout(() => {
        navigate('/login')
      }, 2000)
    } catch (error: any) {
      console.error('Failed to confirm password reset:', error)
      handleApiError(error, showError, '비밀번호 재설정에 실패했습니다')
    } finally {
      setIsConfirmLoading(false)
    }
  }

  const handleBackToLogin = () => {
    navigate('/login')
  }

  const handleBackToEmail = () => {
    setCurrentStep('email')
    setToken('')
    setNewPassword('')
    setConfirmPassword('')
    setTokenError('')
    setNewPasswordError('')
    setConfirmPasswordError('')
  }

  const isEmailFormValid = email.trim().length > 0 && /\S+@\S+\.\S+/.test(email)
  const isVerificationFormValid = token.trim().length > 0 &&
    newPassword.length >= 8 &&
    confirmPassword.trim().length > 0 &&
    newPassword === confirmPassword

  return (
    <div className="w-full h-screen flex">
      {/* Left Sidebar */}
      <div className="w-[800px] flex-shrink-0" style={{ backgroundColor: designTokens.colors.primary.dark }} />

      {/* Right Content Area */}
      <div className="flex-1 flex flex-col justify-center items-center gap-10" style={{ backgroundColor: designTokens.colors.gray[0] }}>
        {/* Reset Password Card */}
        <div className="w-[500px] bg-white rounded-md p-[50px_40px_40px] shadow-sm">
          {/* Header */}
          <div className="flex justify-between items-center pb-5 mb-[15px]">
            <h1
              className="text-[32px] font-bold text-black"
              style={{
                fontFamily: 'Pretendard GOV',
                lineHeight: '1.193359375em'
              }}
            >
              비밀번호 재설정
            </h1>

            {/* Logo */}
            <img
              src="/images/logo-big.png"
              alt="DOT Logo"
              className="h-[33px] w-auto"
            />
          </div>

          {/* Step 1: Email Input */}
          {currentStep === 'email' && (
            <form onSubmit={handleSendResetEmail} className="space-y-5">
              {/* Description */}
              <div className="text-sm text-[#666666] mb-6">
                <p style={{ fontFamily: 'Pretendard GOV', lineHeight: '1.193359375em' }}>
                  등록된 이메일 주소를 입력하시면 비밀번호 재설정을 위한 인증번호를 보내드립니다.
                </p>
              </div>

              {/* Email Field */}
              <div className="space-y-2">
                <div className="flex items-center gap-1">
                  <label
                    htmlFor="reset-email"
                    className="text-base font-normal text-[#1B1B1B]"
                    style={{
                      fontFamily: 'Pretendard GOV',
                      lineHeight: '1.193359375em'
                    }}
                  >
                    이메일
                  </label>
                  <span
                    className="text-xs text-[#E53935]"
                    style={{
                      fontFamily: 'Pretendard GOV',
                      lineHeight: '1.193359375em'
                    }}
                  >
                    *
                  </span>
                </div>

                <Input
                  id="reset-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="이메일을 입력해주세요."
                  size="large"
                  state={emailError ? 'error' : 'default'}
                  disabled={isRequestLoading}
                  style={{
                    height: '48px',
                    fontSize: '18px',
                    fontFamily: 'Pretendard GOV',
                    lineHeight: '1.193359375em'
                  }}
                />

                {emailError && (
                  <p className="text-sm text-[#E53935] mt-1">{emailError}</p>
                )}
              </div>

              {/* Submit Button */}
              <div className="space-y-5 pt-[15px]">
                <Button
                  type="submit"
                  size="large"
                  disabled={isRequestLoading || !isEmailFormValid}
                  className="w-full h-[48px] bg-[#141E2F] border-2 border-[#141E2F] text-[#F6F6F7] font-semibold text-xl hover:bg-[#1a2640] disabled:opacity-50"
                  style={{
                    fontFamily: 'Pretendard GOV',
                    lineHeight: '1.193359375em'
                  }}
                >
                  {isRequestLoading ? '전송 중...' : '인증번호 전송'}
                </Button>

                <button
                  type="button"
                  onClick={handleBackToLogin}
                  className="w-full text-xs text-[#131416] text-center hover:underline flex items-center justify-center gap-2"
                  style={{
                    fontFamily: 'Pretendard GOV',
                    lineHeight: '1.193359375em'
                  }}
                  disabled={isRequestLoading}
                >
                  <ArrowLeft className="w-3 h-3" />
                  로그인으로 돌아가기
                </button>
              </div>
            </form>
          )}

          {/* Step 2: Verification and New Password */}
          {currentStep === 'verify' && (
            <form onSubmit={handleConfirmReset} className="space-y-5">
              {/* Success Message */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <div>
                    <h4 className="text-sm font-semibold text-green-800" style={{ fontFamily: 'Pretendard GOV' }}>
                      인증번호가 전송되었습니다
                    </h4>
                    <p className="text-xs text-green-600 mt-1" style={{ fontFamily: 'Pretendard GOV' }}>
                      {email}로 전송된 인증번호를 확인해주세요.
                    </p>
                  </div>
                </div>
              </div>

              {/* Token Field */}
              <div className="space-y-2">
                <div className="flex items-center gap-1">
                  <label
                    htmlFor="reset-token"
                    className="text-base font-normal text-[#1B1B1B]"
                    style={{
                      fontFamily: 'Pretendard GOV',
                      lineHeight: '1.193359375em'
                    }}
                  >
                    인증번호
                  </label>
                  <span
                    className="text-xs text-[#E53935]"
                    style={{
                      fontFamily: 'Pretendard GOV',
                      lineHeight: '1.193359375em'
                    }}
                  >
                    *
                  </span>
                </div>

                <Input
                  id="reset-token"
                  type="text"
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  placeholder="인증번호를 입력해주세요."
                  size="large"
                  state={tokenError ? 'error' : 'default'}
                  disabled={isConfirmLoading}
                  style={{
                    height: '48px',
                    fontSize: '18px',
                    fontFamily: 'Pretendard GOV',
                    lineHeight: '1.193359375em'
                  }}
                />

                {tokenError && (
                  <p className="text-sm text-[#E53935] mt-1">{tokenError}</p>
                )}
              </div>

              {/* New Password Field */}
              <div className="space-y-2">
                <div className="flex items-center gap-1">
                  <label
                    htmlFor="new-password"
                    className="text-base font-normal text-[#1B1B1B]"
                    style={{
                      fontFamily: 'Pretendard GOV',
                      lineHeight: '1.193359375em'
                    }}
                  >
                    새 비밀번호
                  </label>
                  <span
                    className="text-xs text-[#E53935]"
                    style={{
                      fontFamily: 'Pretendard GOV',
                      lineHeight: '1.193359375em'
                    }}
                  >
                    *
                  </span>
                </div>

                <div className="relative">
                  <Input
                    id="new-password"
                    type={showNewPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="새 비밀번호를 입력해주세요. (최소 8자, 대소문자+숫자 포함)"
                    size="large"
                    state={newPasswordError ? 'error' : 'default'}
                    disabled={isConfirmLoading}
                    style={{
                      height: '48px',
                      fontSize: '18px',
                      fontFamily: 'Pretendard GOV',
                      lineHeight: '1.193359375em',
                      paddingRight: '48px'
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showNewPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>

                {newPasswordError && (
                  <p className="text-sm text-[#E53935] mt-1">{newPasswordError}</p>
                )}

                <p className="text-xs text-[#666666]" style={{ fontFamily: 'Pretendard GOV' }}>
                  비밀번호는 최소 8자 이상이며, 대문자, 소문자, 숫자를 포함해야 합니다.
                </p>
              </div>

              {/* Confirm Password Field */}
              <div className="space-y-2">
                <div className="flex items-center gap-1">
                  <label
                    htmlFor="confirm-password"
                    className="text-base font-normal text-[#1B1B1B]"
                    style={{
                      fontFamily: 'Pretendard GOV',
                      lineHeight: '1.193359375em'
                    }}
                  >
                    새 비밀번호 확인
                  </label>
                  <span
                    className="text-xs text-[#E53935]"
                    style={{
                      fontFamily: 'Pretendard GOV',
                      lineHeight: '1.193359375em'
                    }}
                  >
                    *
                  </span>
                </div>

                <div className="relative">
                  <Input
                    id="confirm-password"
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="새 비밀번호를 다시 입력해주세요."
                    size="large"
                    state={confirmPasswordError ? 'error' : 'default'}
                    disabled={isConfirmLoading}
                    style={{
                      height: '48px',
                      fontSize: '18px',
                      fontFamily: 'Pretendard GOV',
                      lineHeight: '1.193359375em',
                      paddingRight: '48px'
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>

                {confirmPasswordError && (
                  <p className="text-sm text-[#E53935] mt-1">{confirmPasswordError}</p>
                )}
              </div>

              {/* Submit Button */}
              <div className="space-y-3 pt-[15px]">
                <Button
                  type="submit"
                  size="large"
                  disabled={isConfirmLoading || !isVerificationFormValid}
                  className="w-full h-[48px] bg-[#141E2F] border-2 border-[#141E2F] text-[#F6F6F7] font-semibold text-xl hover:bg-[#1a2640] disabled:opacity-50"
                  style={{
                    fontFamily: 'Pretendard GOV',
                    lineHeight: '1.193359375em'
                  }}
                >
                  {isConfirmLoading ? '재설정 중...' : '비밀번호 재설정'}
                </Button>

                <button
                  type="button"
                  onClick={handleBackToEmail}
                  className="w-full text-xs text-[#131416] text-center hover:underline flex items-center justify-center gap-2"
                  style={{
                    fontFamily: 'Pretendard GOV',
                    lineHeight: '1.193359375em'
                  }}
                  disabled={isConfirmLoading}
                >
                  <ArrowLeft className="w-3 h-3" />
                  이메일 입력으로 돌아가기
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}

export default ResetPasswordPage